import { isTutorialSlug, type CompletedBoard } from './play/playHistory';
import { shuffled } from './shuffle';

/*
  The one ask under a finished puzzle, on the website and in the app.

  Each solve shows the next of the asks this player is eligible for, in the order below, so a run of
  solves walks through all of them rather than repeating one. The turn is the number of other boards
  this player's history shows finished, tutorials aside, so it advances once per board solved and
  stays put when a solved board is reloaded. The first solve lands on the first ask, so a new player
  whose answer we can record is always asked how they heard about us.

  Whether each ask applies is the platform's to say, since the facts live in different places; which
  one is shown, and what it says, is decided here so the two clients can't drift apart.
*/

export type SolvedCTAKind =
  | 'heard-about'
  | 'create-account'
  | 'subscribe'
  | 'daily-email'
  | 'add-to-home-screen'
  | 'daily-reminder'
  | 'community';

export interface SolvedCTAEligibility {
  /** Analytics will record the answer, and this player hasn't given one. */
  canAskHeardAbout: boolean;
  signedIn: boolean;
  subscribed: boolean;
  onEmailList: boolean;
  /** The daily reminder notification can be turned on from here, without a trip to the system settings. */
  canEnableReminder: boolean;
  /** A phone browser we can walk through installing. Never true in the app, which is already installed. */
  canAddToHomeScreen: boolean;
}

const CTAS: { kind: SolvedCTAKind; eligible: (e: SolvedCTAEligibility) => boolean }[] = [
  { kind: 'heard-about', eligible: (e) => e.canAskHeardAbout },
  { kind: 'create-account', eligible: (e) => !e.signedIn },
  { kind: 'daily-reminder', eligible: (e) => e.canEnableReminder },
  { kind: 'daily-email', eligible: (e) => !e.onEmailList },
  { kind: 'subscribe', eligible: (e) => !e.subscribed },
  { kind: 'add-to-home-screen', eligible: (e) => e.canAddToHomeScreen },
  { kind: 'community', eligible: () => true },
];

/** Whether this player could be shown the `kind` ask at all, before the rotation picks one. */
export function isSolvedCTAEligible(kind: SolvedCTAKind, eligibility: SolvedCTAEligibility): boolean {
  return CTAS.some((cta) => cta.kind === kind && cta.eligible(eligibility));
}

/** The ask to show under board `boardId`, or null when there is nothing to ask. */
export function chooseSolvedCTA(
  eligibility: SolvedCTAEligibility,
  completed: CompletedBoard[],
  boardId: number
): SolvedCTAKind | null {
  const eligible = CTAS.filter((cta) => cta.eligible(eligibility));
  if (eligible.length === 0) return null;
  // The board just finished is left out, so the answer doesn't depend on whether its play was saved yet.
  const turn = completed.filter((board) => board.boardId !== boardId && !isTutorialSlug(board.boardSlug)).length;
  return eligible[turn % eligible.length].kind;
}

/** What most of the asks say: a heading and a line under it. */
export interface CTAText {
  title: string;
  text: string;
}

export const CREATE_ACCOUNT_CTA = {
  title: "Don't lose your solves",
  text: 'Save your progress, replay puzzles, and play across multiple devices.',
  signUp: 'Create Free Account',
  logIn: 'Log In',
};

export const SUBSCRIBE_CTA = {
  title: 'Unlock everything!',
  text: 'Access hundreds of puzzles in the archive. Unlock the highlighter tool. Get early access to new games.',
  button: 'Subscribe Now',
};

export const ADD_TO_HOME_SCREEN_CTA = {
  title: 'All our games, as an app',
  text: 'Add Inkwell to your home screen.',
  button: 'Add to Home Screen',
};

export const DAILY_EMAIL_CTA = {
  title: 'Come back tomorrow!',
  /** The checkbox that turns the reminder on for an account, whose address is already known, on the line below. */
  toggle: 'Get a daily reminder email to:',
  /** Signed out, the player types an address in and signs up with `button`. */
  ask: 'Get a daily reminder:',
  placeholder: 'Your email address',
  button: 'Sign Up',
  working: 'Subscribing...',
  done: 'Subscribed!',
};

export const DISCORD_INVITE_URL = 'https://discord.gg/9GKjVgtDjK';
export const FACEBOOK_GROUP_URL = 'https://www.facebook.com/groups/inkwellgames';

export type Community = 'discord' | 'facebook';

/** Read as one sentence, with each community's icon before its link: "Did you know Inkwell has a [Discord server]? And a [Facebook group]?" */
export const COMMUNITY_CTA = {
  title: 'Join our community',
  links: [
    { community: 'discord', lead: 'Did you know Inkwell has a', label: 'Discord server', url: DISCORD_INVITE_URL },
    { community: 'facebook', lead: 'And a', label: 'Facebook group', url: FACEBOOK_GROUP_URL },
  ] satisfies { community: Community; lead: string; label: string; url: string }[],
};

/*
  The daily reminder, at each stage of turning it on. It's a web push on the website and a local
  notification in the app, and the stages in between differ: a browser's prompt can be closed without
  an answer, and the app's refusal points to Settings.
*/

const REMINDER_ASK: CTAText = {
  title: 'Never miss a puzzle',
  text: 'Get a gentle nudge each morning with a link to your daily puzzles.',
};
const REMINDER_DONE: CTAText = {
  title: "You're all set",
  text: "We'll let you know each morning when the new puzzles are ready.",
};

export type WebReminderStage = 'ask' | 'working' | 'done' | 'failed' | 'dismissed';

export const WEB_REMINDER_CTA: Record<WebReminderStage, CTAText & { button?: string }> = {
  ask: { ...REMINDER_ASK, button: 'Turn on notifications' },
  working: {
    title: REMINDER_ASK.title,
    text: 'Your browser is asking for permission now — choose Allow to finish.',
    button: 'Just a sec…',
  },
  dismissed: {
    title: 'Almost there',
    text: 'That browser prompt closed before you chose. Tap below and choose Allow to finish.',
    button: 'Try again',
  },
  done: REMINDER_DONE,
  failed: {
    title: "Couldn't turn those on",
    text: 'Notifications are blocked for this site. You can allow them in your browser settings and try again.',
  },
};

export type AppReminderStage = 'ask' | 'working' | 'done' | 'denied' | 'error';

export const APP_REMINDER_CTA: Record<AppReminderStage, CTAText & { button?: string }> = {
  ask: { ...REMINDER_ASK, button: 'Turn on notifications' },
  working: {
    title: REMINDER_ASK.title,
    text: 'Your device is asking for permission now — choose Allow to finish.',
    button: 'Just a sec…',
  },
  done: REMINDER_DONE,
  denied: {
    title: 'Notifications are off',
    text: 'To get a daily reminder, allow notifications for Inkwell in Settings.',
    button: 'Open Settings',
  },
  error: {
    title: "Couldn't turn those on",
    text: "We couldn't set up your reminder just now. Please try again.",
    button: 'Try again',
  },
};

export const HEARD_ABOUT_OTHER = 'Other';

/*
  The survey's choices: the wording shown, which is also what PostHog records, and the stable key a
  profile stores (the `heard_about_source` enum). The wording matches the PostHog survey this replaced,
  so old and new answers break down together there; rewording one only changes its label here.
*/
export const HEARD_ABOUT_SOURCES = {
  YouTube: 'youtube',
  'Newsletter or blog post': 'newsletter_or_blog',
  'Friend or family member': 'friend_or_family',
  'News article or media coverage': 'news_or_media',
  'Facebook/Instagram': 'facebook_instagram',
  TikTok: 'tiktok',
  'Search engine (Google, etc.)': 'search_engine',
  'AI/LLM (ChatGPT, Claude, Gemini, etc.)': 'ai_llm',
  [HEARD_ABOUT_OTHER]: 'other',
} as const;

export type HeardAboutSource = (typeof HEARD_ABOUT_SOURCES)[keyof typeof HEARD_ABOUT_SOURCES];

const HEARD_ABOUT_OPTIONS = Object.keys(HEARD_ABOUT_SOURCES).filter((label) => label !== HEARD_ABOUT_OTHER);

export const HEARD_ABOUT_CTA = {
  question: 'How did you hear about Inkwell?',
  submit: 'Submit',
  thanks: { title: 'Thank you!', text: 'That helps us find more puzzlers like you.' } satisfies CTAText,
};

/** The options in a fresh random order, so no source gains from being listed first; 'Other' stays last. */
export const shuffledHeardAboutOptions = () => [...shuffled(HEARD_ABOUT_OPTIONS), HEARD_ABOUT_OTHER];

/** The answer, as it goes on the event and, for a signed-in player, onto their PostHog person. */
export type HeardAboutAnswer = { heard_about: string; heard_about_other?: string };

/*
  On for a person whose `heard_about` is set, so a signed-in player who answered on another device isn't
  asked again. Flags are evaluated against person properties on PostHog's side; the properties themselves
  never come back. Read without reporting an exposure: it's a lookup, not an experiment.
*/
export const HEARD_ABOUT_ANSWERED_FLAG = 'heard-about-answered';
